/**
 * gpu-content.ts — innhold for /gpu (norsk + engelsk)
 *
 * ⚠ PLASSHOLDERE: GPU-klasser (S/M/L), GPU-minne, vCPU, RAM, lagring, priser, deploy-
 * metoder (container/Git), støttede rammeverk, region, tilgjengelighet/kø, SLA, FAQ og
 * kontaktadresse er foreløpige og MÅ erstattes med det som faktisk leveres før siden
 * publiseres. Utviklere velger på teknisk spesifikasjon, så alt må leses gjennom av eier.
 * All redigering gjøres her — komponentene har ingen egne tekster eller tall.
 *
 * MERKEVARER: siden bruker generiske GPU-klasser og nevner ingen GPU-produsent eller
 * modell, siden vi ikke kjenner maskinvaren. Legges produsentnavn til senere, må
 * varemerkereglene til den aktuelle produsenten følges.
 *
 * Offentlig side: statisk innhold i frontend er tillatt. Når prisene skal styres fra
 * systemet, erstattes denne filen av et API-kall (GpuContent i src/types/gpu.ts er
 * kontrakten).
 */

import type { Locale } from '@/types/landing';
import type { GpuContent } from '@/types/gpu';

/** TODO: bekreft riktig kontaktadresse (admin@ntune.no er den eneste i koden i dag). */
const CONTACT = 'mailto:admin@ntune.no?subject=inGPU';

const no: GpuContent = {
  seoTitle: 'inGPU — GPU-deploy fra inSuite',
  ui: {
    skipToContent: 'Hopp til innhold',
    navLabel: 'Hovednavigasjon',
    languageLabel: 'Språk',
    themeLabel: 'Tema',
    themeToDark: 'Bytt til mørkt tema',
    themeToLight: 'Bytt til lyst tema',
    login: 'Logg inn',
  },
  breadcrumb: { label: 'Brødsmulesti', links: [{ label: 'Hjem', href: '/' }], current: 'inGPU' },
  sectionNav: {
    label: 'Seksjoner',
    items: [
      { id: 'oversikt', label: 'Oversikt' },
      { id: 'funksjoner', label: 'Funksjoner' },
      { id: 'pakker', label: 'Pakker' },
      { id: 'sammenlign', label: 'Sammenlign' },
      { id: 'kom-i-gang', label: 'Kom i gang' },
      { id: 'sporsmal', label: 'Spørsmål' },
    ],
    cta: { label: 'Se pakkene', href: '#pakker' },
  },
  hero: {
    heading: 'inGPU',
    subheading:
      'Deploy GPU-arbeidslaster uten å bygge din egen maskinpark: velg GPU-klasse, last opp containeren, og la oss ta driften.',
    primaryCta: { label: 'Se pakker og priser', href: '#pakker' },
    secondaryCta: { label: 'Dette får du', href: '#funksjoner' },
    illustrationLabel: 'Illustrasjon av et GPU-kort, en server og en deploy-kjede',
  },
  overview: {
    eyebrow: 'Oversikt',
    heading: 'GPU-kraft. Uten maskinparken.',
    body: [
      'inGPU er hosting for arbeidslaster som trenger GPU, som AI-inferens, finjustering av modeller, rendering og tung beregning. Du leverer containeren, og vi kjører den på riktig maskinvare.',
      'Trenger du ikke GPU? Da er inNode eller inVPS et enklere og rimeligere valg.',
    ],
  },
  features: {
    eyebrow: 'Funksjoner',
    heading: 'Dette får du',
    intro: 'Det som skal til for å kjøre GPU-arbeidslaster i produksjon.',
    panelLabel: 'Illustrasjon av et GPU-kort og en server',
    items: [
      { title: 'Deploy med container', body: 'Last opp en container, eller koble Git-repoet, og vi bygger og starter arbeidslasten.' },
      { title: 'Velg GPU-klasse', body: 'Velg størrelsen som passer modellen din, og gå opp en klasse når du trenger mer minne.' },
      { title: 'Rask lagring', body: 'Modeller og datasett ligger på rask SSD-lagring, så oppstart og lasting går fort.' },
      { title: 'API-endepunkt', body: 'Eksponer modellen som et sikkert endepunkt med SSL, klart til å brukes av appene dine.' },
      { title: 'Overvåking', body: 'Følg GPU-bruk, minne og tid per forespørsel, og se loggene fra arbeidslasten.' },
      { title: 'Privat nettverk', body: 'Hold dataene dine inne i et privat nettverk, med brannmur du styrer selv.' },
      { title: 'Skalering', body: 'Gå opp en klasse eller legg til flere instanser når arbeidslasten vokser.' },
    ],
  },
  promo: {
    title: 'Trenger du bare en vanlig server?',
    body: 'Uten GPU er inVPS enklere og rimeligere, med full rotilgang.',
    cta: { label: 'Se inVPS', href: '/vps' },
  },
  plans: {
    eyebrow: 'Pakker',
    heading: 'Pakker og priser',
    intro: 'Velg GPU-klassen som passer, og gå opp en klasse når modellene dine vokser.',
    cardsHeading: 'Velg pakke',
    fromLabel: 'Fra',
    footnote: '*Prisene er veiledende, per måned og eksklusiv mva.',
    items: [
      {
        id: 's',
        name: 'GPU S',
        summary: 'For testing og små modeller.',
        price: '1 490',
        priceNote: 'kr / mnd',
        features: ['1 GPU, 16 GB minne', '4 vCPU', '32 GB RAM', '200 GB SSD', 'SSL-endepunkt'],
        cta: { label: 'Bestill GPU S', href: CONTACT },
      },
      {
        id: 'm',
        name: 'GPU M',
        summary: 'For inferens i produksjon.',
        price: '3 990',
        priceNote: 'kr / mnd',
        inheritsLabel: 'Alt i GPU S, pluss',
        features: ['1 GPU, 48 GB minne', '8 vCPU', '96 GB RAM', '500 GB SSD', 'Overvåking og logger'],
        cta: { label: 'Bestill GPU M', href: CONTACT },
        highlighted: true,
        highlightLabel: 'Anbefalt',
      },
      {
        id: 'l',
        name: 'GPU L',
        summary: 'For trening og store modeller.',
        price: '9 990',
        priceNote: 'kr / mnd',
        inheritsLabel: 'Alt i GPU M, pluss',
        features: ['2 GPU-er, 96 GB minne', '16 vCPU', '192 GB RAM', '2 TB SSD', 'Prioritert support'],
        cta: { label: 'Bestill GPU L', href: CONTACT },
      },
    ],
  },
  comparison: {
    eyebrow: 'Sammenlign',
    heading: 'Sammenlign pakkene',
    intro: 'Se hva hver GPU-klasse inneholder.',
    featureLabel: 'Funksjon',
    yes: 'Inkludert',
    no: 'Ikke inkludert',
    rows: [
      { label: 'Maskinvare', values: [], group: true },
      { label: 'GPU-er', values: ['1', '1', '2'] },
      { label: 'GPU-minne', values: ['16 GB', '48 GB', '96 GB'] },
      { label: 'vCPU', values: ['4', '8', '16'] },
      { label: 'RAM', values: ['32 GB', '96 GB', '192 GB'] },
      { label: 'SSD-lagring', values: ['200 GB', '500 GB', '2 TB'] },
      { label: 'Funksjoner og support', values: [], group: true },
      { label: 'Deploy med container', values: [true, true, true] },
      { label: 'SSL-endepunkt', values: [true, true, true] },
      { label: 'Overvåking og logger', values: [false, true, true] },
      { label: 'Privat nettverk', values: [false, true, true] },
      { label: 'Prioritert support', values: [false, false, true] },
    ],
  },
  steps: {
    eyebrow: 'Kom i gang',
    heading: 'Fra container til kjørende modell',
    intro: 'Fire steg, og du trenger ikke kjøpe eller drifte egen maskinvare.',
    panelLabel: 'Illustrasjon av et GPU-kort og en server',
    items: [
      { title: 'Velg GPU-klasse', body: 'Velg pakken som passer minnebehovet til modellen din.' },
      { title: 'Last opp containeren', body: 'Last opp containeren eller koble Git-repoet, og oppgi startkommando.' },
      { title: 'Vi setter opp og starter', body: 'Vi setter av GPU-en, starter arbeidslasten og gir deg et sikkert endepunkt.' },
      { title: 'Kjør og følg med', body: 'Bruk endepunktet fra appene dine, og følg bruk og logger i oversikten.' },
    ],
  },
  useCases: {
    eyebrow: 'Bruksområder',
    heading: 'Slik brukes det',
    items: [
      {
        icon: 'ai',
        title: 'AI-inferens',
        body: 'Kjør språk-, bilde- og lydmodeller som et endepunkt appene dine kan kalle.',
        link: { label: 'Be om tilbud', href: CONTACT },
      },
      {
        icon: 'chip',
        title: 'Trening og finjustering',
        body: 'Tilpass modeller til dine egne data på en GPU du slipper å eie selv.',
        link: { label: 'Be om tilbud', href: CONTACT },
      },
      {
        icon: 'process',
        title: 'Rendering og tung beregning',
        body: 'Kjør rendering, simuleringer og batchjobber som trenger parallell regnekraft.',
        link: { label: 'Kontakt oss', href: CONTACT },
      },
    ],
  },
  related: {
    eyebrow: 'Relatert',
    heading: 'Relaterte tjenester',
    items: [
      { icon: 'virtual', name: 'inVPS — virtuelle servere', href: '/vps' },
      { icon: 'nodejs', name: 'inNode — Node.js-hosting', href: '/nodejs' },
      { icon: 'server', name: 'inHost — webhotell', href: '/webhotell' },
      { icon: 'globe', name: 'inDomain — domener', href: '/domener/sok' },
    ],
  },
  faq: {
    eyebrow: 'Spørsmål',
    heading: 'Vanlige spørsmål',
    items: [
      {
        question: 'Hva er forskjellen på inGPU og inVPS?',
        answer:
          'inGPU gir deg tilgang til GPU og kjører containeren din, mens inVPS er en vanlig virtuell server med full rotilgang uten GPU.',
      },
      { question: 'Hvilken GPU-klasse trenger jeg?', answer: 'Det avhenger av hvor mye minne modellen din trenger. Ta kontakt, så hjelper vi deg å velge.' },
      { question: 'Hvilke rammeverk støttes?', answer: 'Alt som kjører i en container og bruker GPU. Ta kontakt for detaljer om drivere og versjoner.' },
      { question: 'Hvor lang tid tar det å få en GPU?', answer: 'Det avhenger av tilgjengelighet. Ta kontakt for oppdatert leveringstid.' },
      { question: 'Hvordan bestiller jeg?', answer: 'Send oss en e-post med pakken du ønsker, så tar vi kontakt for å sette opp arbeidslasten din.' },
    ],
  },
  cta: {
    heading: 'Klar til å deploye?',
    body: 'Kontakt oss, så setter vi opp GPU-en din.',
    primary: { label: 'Kontakt oss', href: CONTACT },
  },
  footer: {
    tagline: 'inGPU — GPU-deploy der du leverer containeren og vi tar driften.',
    legal: 'inSuite',
    platformLabel: 'inSuite',
    sectionsLabel: 'inGPU',
  },
};

const en: GpuContent = {
  seoTitle: 'inGPU — GPU deploy from inSuite',
  ui: {
    skipToContent: 'Skip to content',
    navLabel: 'Main navigation',
    languageLabel: 'Language',
    themeLabel: 'Theme',
    themeToDark: 'Switch to dark theme',
    themeToLight: 'Switch to light theme',
    login: 'Log in',
  },
  breadcrumb: { label: 'Breadcrumb', links: [{ label: 'Home', href: '/' }], current: 'inGPU' },
  sectionNav: {
    label: 'Sections',
    items: [
      { id: 'oversikt', label: 'Overview' },
      { id: 'funksjoner', label: 'Features' },
      { id: 'pakker', label: 'Plans' },
      { id: 'sammenlign', label: 'Compare' },
      { id: 'kom-i-gang', label: 'Get started' },
      { id: 'sporsmal', label: 'FAQ' },
    ],
    cta: { label: 'See the plans', href: '#pakker' },
  },
  hero: {
    heading: 'inGPU',
    subheading:
      'Deploy GPU workloads without building your own hardware: pick a GPU class, upload your container, and let us run the operations.',
    primaryCta: { label: 'See plans and pricing', href: '#pakker' },
    secondaryCta: { label: 'What you get', href: '#funksjoner' },
    illustrationLabel: 'Illustration of a GPU card, a server and a deploy chain',
  },
  overview: {
    eyebrow: 'Overview',
    heading: 'GPU power. Without the hardware.',
    body: [
      'inGPU is hosting for workloads that need a GPU, such as AI inference, model fine-tuning, rendering and heavy computation. You deliver the container, and we run it on the right hardware.',
      'Do you not need a GPU? Then inNode or inVPS is a simpler and cheaper choice.',
    ],
  },
  features: {
    eyebrow: 'Features',
    heading: 'What you get',
    intro: 'What it takes to run GPU workloads in production.',
    panelLabel: 'Illustration of a GPU card and a server',
    items: [
      { title: 'Deploy with a container', body: 'Upload a container, or connect your Git repository, and we build and start the workload.' },
      { title: 'Choose your GPU class', body: 'Pick the size that fits your model, and move up a class when you need more memory.' },
      { title: 'Fast storage', body: 'Models and datasets live on fast SSD storage, so startup and loading are quick.' },
      { title: 'API endpoint', body: 'Expose the model as a secure endpoint with SSL, ready for your apps to use.' },
      { title: 'Monitoring', body: 'Follow GPU usage, memory and time per request, and see the logs from the workload.' },
      { title: 'Private network', body: 'Keep your data inside a private network, with a firewall you control.' },
      { title: 'Scaling', body: 'Move up a class or add more instances as the workload grows.' },
    ],
  },
  promo: {
    title: 'Only need a regular server?',
    body: 'Without a GPU, inVPS is simpler and cheaper, with full root access.',
    cta: { label: 'See inVPS', href: '/vps' },
  },
  plans: {
    eyebrow: 'Plans',
    heading: 'Plans and pricing',
    intro: 'Pick the GPU class that fits, and move up a class as your models grow.',
    cardsHeading: 'Choose a plan',
    fromLabel: 'From',
    footnote: '*Prices are indicative, per month and exclude VAT.',
    items: [
      {
        id: 's',
        name: 'GPU S',
        summary: 'For testing and small models.',
        price: '1,490',
        priceNote: 'NOK / month',
        features: ['1 GPU, 16 GB memory', '4 vCPU', '32 GB RAM', '200 GB SSD', 'SSL endpoint'],
        cta: { label: 'Order GPU S', href: CONTACT },
      },
      {
        id: 'm',
        name: 'GPU M',
        summary: 'For inference in production.',
        price: '3,990',
        priceNote: 'NOK / month',
        inheritsLabel: 'Everything in GPU S, plus',
        features: ['1 GPU, 48 GB memory', '8 vCPU', '96 GB RAM', '500 GB SSD', 'Monitoring and logs'],
        cta: { label: 'Order GPU M', href: CONTACT },
        highlighted: true,
        highlightLabel: 'Recommended',
      },
      {
        id: 'l',
        name: 'GPU L',
        summary: 'For training and large models.',
        price: '9,990',
        priceNote: 'NOK / month',
        inheritsLabel: 'Everything in GPU M, plus',
        features: ['2 GPUs, 96 GB memory', '16 vCPU', '192 GB RAM', '2 TB SSD', 'Priority support'],
        cta: { label: 'Order GPU L', href: CONTACT },
      },
    ],
  },
  comparison: {
    eyebrow: 'Compare',
    heading: 'Compare the plans',
    intro: 'See what each GPU class includes.',
    featureLabel: 'Feature',
    yes: 'Included',
    no: 'Not included',
    rows: [
      { label: 'Hardware', values: [], group: true },
      { label: 'GPUs', values: ['1', '1', '2'] },
      { label: 'GPU memory', values: ['16 GB', '48 GB', '96 GB'] },
      { label: 'vCPU', values: ['4', '8', '16'] },
      { label: 'RAM', values: ['32 GB', '96 GB', '192 GB'] },
      { label: 'SSD storage', values: ['200 GB', '500 GB', '2 TB'] },
      { label: 'Features and support', values: [], group: true },
      { label: 'Deploy with a container', values: [true, true, true] },
      { label: 'SSL endpoint', values: [true, true, true] },
      { label: 'Monitoring and logs', values: [false, true, true] },
      { label: 'Private network', values: [false, true, true] },
      { label: 'Priority support', values: [false, false, true] },
    ],
  },
  steps: {
    eyebrow: 'Get started',
    heading: 'From container to running model',
    intro: 'Four steps, and you do not have to buy or run your own hardware.',
    panelLabel: 'Illustration of a GPU card and a server',
    items: [
      { title: 'Choose a GPU class', body: 'Pick the plan that fits your model’s memory needs.' },
      { title: 'Upload your container', body: 'Upload the container or connect your Git repository, and give the start command.' },
      { title: 'We set up and start', body: 'We reserve the GPU, start the workload and give you a secure endpoint.' },
      { title: 'Run and follow along', body: 'Use the endpoint from your apps, and follow usage and logs in the overview.' },
    ],
  },
  useCases: {
    eyebrow: 'Use cases',
    heading: 'How it is used',
    items: [
      {
        icon: 'ai',
        title: 'AI inference',
        body: 'Run language, image and audio models as an endpoint your apps can call.',
        link: { label: 'Request a quote', href: CONTACT },
      },
      {
        icon: 'chip',
        title: 'Training and fine-tuning',
        body: 'Adapt models to your own data on a GPU you do not have to own.',
        link: { label: 'Request a quote', href: CONTACT },
      },
      {
        icon: 'process',
        title: 'Rendering and heavy computation',
        body: 'Run rendering, simulations and batch jobs that need parallel computing power.',
        link: { label: 'Contact us', href: CONTACT },
      },
    ],
  },
  related: {
    eyebrow: 'Related',
    heading: 'Related services',
    items: [
      { icon: 'virtual', name: 'inVPS — virtual servers', href: '/vps' },
      { icon: 'nodejs', name: 'inNode — Node.js hosting', href: '/nodejs' },
      { icon: 'server', name: 'inHost — web hosting', href: '/webhotell' },
      { icon: 'globe', name: 'inDomain — domains', href: '/domener/sok' },
    ],
  },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    items: [
      {
        question: 'What is the difference between inGPU and inVPS?',
        answer:
          'inGPU gives you access to a GPU and runs your container, while inVPS is a regular virtual server with full root access and no GPU.',
      },
      { question: 'Which GPU class do I need?', answer: 'It depends on how much memory your model needs. Get in touch, and we will help you choose.' },
      { question: 'Which frameworks are supported?', answer: 'Anything that runs in a container and uses the GPU. Get in touch for details on drivers and versions.' },
      { question: 'How long does it take to get a GPU?', answer: 'It depends on availability. Get in touch for the current lead time.' },
      { question: 'How do I order?', answer: 'Send us an email with the plan you want, and we will get in touch to set up your workload.' },
    ],
  },
  cta: {
    heading: 'Ready to deploy?',
    body: 'Contact us and we will set up your GPU.',
    primary: { label: 'Contact us', href: CONTACT },
  },
  footer: {
    tagline: 'inGPU — GPU deploy where you deliver the container and we run the operations.',
    legal: 'inSuite',
    platformLabel: 'inSuite',
    sectionsLabel: 'inGPU',
  },
};

export const GPU_CONTENT: Record<Locale, GpuContent> = { no, en };
